import { List } from "immutable";
import _ from "lodash";
import React from "react";
import * as Comp from "../../../components";
import { ComponentWithValidatedForm } from "../../../components/validation";
import { Contacts } from "../../../constants";
import { CareerCenterJobCategories } from "../../../domain/CareerCenterJobCategories";
import * as Forms from "../../../forms";
import * as M from "../../../models";
import { Api } from "../../../services/Api";
import { Utility } from "../../../utils";

interface OwnProps {
    api: Api;
    cancelLink?: string;
    countries: List<M.Country.Country>;
    includeSubscribe?: boolean;
    isUpdate?: boolean;
    userIsMember: boolean;
    onSubmit: (model: Forms.CareerCenter.PostJobData, body: any) => void;
}

interface OwnState {
}

export class PostJobForm extends ComponentWithValidatedForm<OwnProps, OwnState> {

    // private _isMounted = false;
    private _searchTimeout: NodeJS.Timer;

    public constructor(props: any) {
        super(props);
        const formModel = new Forms.CareerCenter.PostJob();
        this.state = {
            ...this.getInitialState(formModel),
        };
    }

    public async componentDidMount() {
        // this._isMounted = true;
    }

    public componentWillUnmount() {
        // this._isMounted = false;
    }

    public setUserIsMember(userIsMember: boolean) {
        const formModel = { ...this.state.formModel } as Forms.CareerCenter.PostJob;
        formModel.data.userIsMember = userIsMember;
        this.setState({ ...this.state, formModel });
    }

    public setFormDataFromJob(job: M.CareerCenter.JobPost) {
        const formModel = { ...this.state.formModel } as Forms.CareerCenter.PostJob;
        formModel.data.categoryId = job.categoryId;
        formModel.data.title = job.title || "";
        formModel.data.organization = { value: job.organizationId, label: job.organization || "" };
        formModel.data.location = {
            city: job.location.city || "",
            state: job.location.state || "",
            postalCode: job.location.postalCode || "",
            countryCode: {
                label: job.location.country || "",
                value: job.location.countryCode || "",
                isoCode: job.location.countryCode || "US",
            },
        };
        formModel.data.description = job.description || "";
        formModel.data.howToApply = job.howToApply || "";
        formModel.data.refMemberId = job.refMember.memberId;
        formModel.data.refMemberName = job.refMember
            ? Utility.toReactSelectOption(job.refMember, M.Member.MemberHelper.fullName(job.refMember, true), job.refMember.memberId)
            : { value: 0, label: "" };
        formModel.data.subscribe = job.subscribed;
        this.setState({ ...this.state, formModel });
    }

    public render() {
        const categories: M.Editors.SelectItem[] = _.orderBy(CareerCenterJobCategories.Values, "sort").map((c) => ({
            id: c.id,
            render: c.name,
            selected: false,
        }));

        const refMemberFields = this.props.userIsMember
            ? null
            : <Comp.Generic.WrapperLess>
                <hr />
                <h2>ASRS Member Reference</h2>
                <Comp.Editors.FormField name="">
                    <p>
                        Posting a job in the ASRS Career Center is free but requires that you provide an ASRS member reference.
                        Questions? Contact <a href={`mailto:${Contacts.MembershipTeam.address}`}>{Contacts.MembershipTeam.name}</a>.
                    </p>
                </Comp.Editors.FormField>
                <Comp.Editors.SelectField
                    formComponent={this}
                    isAsync
                    name="refMemberName"
                    select={{
                        className: "txt-med",
                        ignoreAccents: false,
                        loadOptions: (search: string) => this.searchMember(search),
                        optionRenderer: (m: M.Member.BasicProfile) => <span>
                                {M.Member.MemberHelper.fullName(m, true)}
                                <br />
                                <span className="mute">{m.username}</span>
                            </span>,
                        placeholder: "",
                    }}
                />
            </Comp.Generic.WrapperLess>;

        const subscribeForm = this.props.includeSubscribe === undefined || this.props.includeSubscribe
            ? <Comp.Generic.WrapperLess>
                <hr />
                <h2>Job Alerts</h2>

                <Comp.Editors.FormField name="">
                    <p>
                        Receive automated email notifications whenever new jobs that match your job category become available.
                        Unsubscribe at any time.
                    </p>
                </Comp.Editors.FormField>
                <Comp.Editors.CheckboxField formComponent={this} name="subscribe" />
            </Comp.Generic.WrapperLess>
            : null;

        return <Comp.Validation.ValidatedForm
                formComponent={this}
                model={this.state.formModel.data}
                onInputChange={(inputName) => this.handleInputChange(inputName)}
                onValidSubmit={(model) => this.handleValidSubmit(model)}
                schema={this.state.formModel.rules}
                ref={(ref: any) => this.form = ref}
            >
                <h1>Post a Job</h1>

                <Comp.Generic.RequiredFields />

                <h2>Position Details</h2>

                <Comp.Editors.RadioOrCheckListField formComponent={this} items={categories} listCss="options-h" name="categoryId" type="radio" />
                <Comp.Editors.TextField formComponent={this} name="title" />
                <Comp.Editors.SelectField
                    formComponent={this}
                    isAsync
                    name="organization"
                    select={{
                        className: "txt-med",
                        ignoreAccents: false,
                        ignoreCase: false,
                        loadOptions: (search: string) => this.searchOrg(search),
                        placeholder: "",
                    }}
                />
                <Comp.Editors.AddressField formComponent={this} excludeAddressLines excludeAddressType name="location" />
                <Comp.Editors.RichTextField formComponent={this} name="description" />
                <Comp.Editors.RichTextField formComponent={this} name="howToApply" />

                {refMemberFields}

                {subscribeForm}

                <hr />

                <Comp.Editors.SubmitField
                    cancelLink={this.props.cancelLink}
                    showProcessing={this.state.formProcessing} label={this.props.isUpdate ? "Save" : "Continue"}
                />

            </Comp.Validation.ValidatedForm>;
    }

    private handleInputChange(inputName: string) {
        if (inputName === "organization") {
            const formModelData = this.getFormModelData<Forms.CareerCenter.PostJobData>();
            this.onOrgChange(formModelData.organization as M.Org.BasicOrganizationProfile);
        }
        else if (inputName === "refMemberName") {
            const formModelData = this.getFormModelData<Forms.CareerCenter.PostJobData>();
            this.onRefMemberChange(formModelData.refMemberName as M.Member.BasicProfile);
        }
    }

    private handleValidSubmit(model: Forms.CareerCenter.PostJobData) {
        const body = {
            categoryId: model.categoryId,
            description: model.description,
            howToApply: model.howToApply,
            organization: model.organization
                ? (model.organization as M.Generic.ReactSelectOption).label
                : undefined,
            organizationId: model.organization
                ? (model.organization as M.Generic.ReactSelectOption).value
                : undefined,
            title: model.title,
            city: model.location.city,
            state: model.location.state,
            postalCode: model.location.postalCode,
            countryCode: model.location.countryCode
                ? (model.location.countryCode as any).value
                : undefined,
            refMemberId: this.props.userIsMember ? undefined : (model.refMemberName as M.Generic.ReactSelectOption).value,
            subscribe: model.subscribe,
        };

        this.props.onSubmit(model, body);
    }

    private onOrgChange(record: M.Org.BasicOrganizationProfile | null) {
        const formModel = Object.assign({}, this.state.formModel);
        formModel.data.organizationId = record === null ? 0 : record.id;
        this.setState({ ...this.state, formModel });
    }

    private onRefMemberChange(record: M.Member.BasicProfile | null) {
        const formModel = Object.assign({}, this.state.formModel);
        formModel.data.refMemberId = record === null ? 0 : record.memberId;
        this.setState({ ...this.state, formModel });
    }

    private searchMember(search: string) {
        if (!search || search.length < 2)
            return Promise.resolve({ options: [] });

        return new Promise((resolve, reject) => {
            // delay the ajax call as to not make a call for each letter typed.
            if (this._searchTimeout !== undefined)
                clearTimeout(this._searchTimeout);

            this._searchTimeout = setTimeout(() => {
                const queryParams = {
                    queryParams: {
                        query: search,
                        includeNonMembers: false,
                    },
                };

                this.props.api.callApi<M.Member.BasicProfile[]>(Api.endpoints.members.search, queryParams)
                    .then((result) => {
                        if (result.isSuccess && result.data) {
                            let options = result.data.map((m) => {
                                let name = Utility.joinSkipEmpty([m.firstName, m.middleName, m.lastName], " ");
                                if (m.degrees)
                                    name += ", " + m.degrees;
                                return Utility.toReactSelectOption(m, name, m.memberId);
                            });

                            options = [...options];
                            resolve({ options });
                        }
                        else
                            throw new Error(result.errors.join("; "));
                    });
            }, 375);
        }).then((data) => {
            return data;
        });
    }

    private searchOrg(search: string) {
        if (!search || search.length < 2)
            return Promise.resolve({ options: [] });

        return new Promise((resolve, reject) => {
            // delay the ajax call as to not make a call for each letter typed.
            if (this._searchTimeout !== undefined)
                clearTimeout(this._searchTimeout);

            this._searchTimeout = setTimeout(() => {
                const queryParams = {
                    queryParams: {
                        query: search,
                        includeNonMembers: false,
                    },
                };

                // for adding new value
                const newOrg: M.Org.BasicOrganizationProfile & M.Generic.ReactSelectOption =
                    Utility.toReactSelectOption({ id: 0, statusId: 0, name: search }, search, 0);

                this.props.api.callApi<M.Org.BasicOrganizationProfile[]>(Api.endpoints.org.search, queryParams)
                    .then((result) => {
                        if (result.isSuccess && result.data) {
                            let options = result.data.map((o) => {
                                return Utility.toReactSelectOption(o, o.name, o.id);
                            });

                            const newOrgMatch = _.find(options, (org) => {
                                return org.name.toLowerCase().trim() === newOrg.label.toLowerCase().trim();
                            });

                            if (newOrgMatch === undefined)
                                options = [newOrg, ...options];

                            options = [...options];
                            resolve({ options });
                        }
                        else
                            throw new Error(result.errors.join("; "));
                    });
            }, 375);
        }).then((data) => {
            return data;
        });
    }
}



// WEBPACK FOOTER //
// ./src/containers/careerCenter/postJob/PostJobForm.tsx